import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * The pages whose bottom strip belongs to a form.
 *
 * Kept here, beside the bar itself, because the only reason this list exists
 * is that the "New lead" shortcut floats in the same place — see
 * `NewLeadFab`, which is the one thing that reads it. A route list rather
 * than a presence flag the bar registers on mount: registration happens in an
 * effect, which means the shortcut paints once before disappearing on every
 * form load, and it cannot be exercised by a renderer that does not run
 * effects, which is the only one this app's tests have.
 *
 * ADD A FORM, ADD IT HERE.
 */
export function pageHasFormActionBar(pathname: string): boolean {
  // Both `/templates/new` and `/templates/:id` are the same form component.
  return pathname === '/leads/new' || pathname === '/contacts/new' || /^\/templates\/[^/]+$/.test(pathname);
}

interface Props {
  /**
   * The left of the strip: anything that is not "finish this" — a delete, a
   * secondary route out. Kept apart from the main actions so a destructive
   * button is never adjacent to the one people click without reading.
   */
  start?: ReactNode;
  /** The right of the strip, in reading order: Cancel, then the primary action. */
  children: ReactNode;
  className?: string;
}

/**
 * A form's actions, kept on screen.
 *
 * These used to sit at the end of the page — on the lead form, at the foot of
 * a column beside three cards — so saving meant scrolling past everything to
 * find the button. The strip follows instead.
 *
 * STICKY, NOT FIXED. A fixed bar has to know the sidebar's width to avoid
 * covering it, and the page has to reserve a matching strip of padding or the
 * last field sits underneath it forever. Sticky needs neither: it floats while
 * there is more page below, then lands in its own place at the end.
 *
 * It must stay a block-level child of a container taller than the window —
 * the form itself. A sticky element inside a CSS grid is bounded by its grid
 * AREA, and a bar on its own row has an area exactly its own height, so it
 * would never move. That is why the lead form's two columns are a grid nested
 * inside the form rather than the form being the grid.
 */
export function FormActionBar({ start, children, className }: Props) {
  return (
    <div
      className={cn(
        // Full-bleed to the edges of <main>, cancelling its padding, so it
        // reads as part of the window frame rather than a card that happens
        // to be last.
        '-mx-4 md:-mx-6',
        'sticky z-20 flex flex-wrap items-center gap-2 border-t border-line bg-surface px-4 py-3 md:px-6',
        // Clear of the phone tab bar (h-14) and the home indicator below it.
        // On a laptop that bar does not exist and this sits on the floor.
        'bottom-[calc(3.5rem+env(safe-area-inset-bottom))] md:bottom-0',
        // Lifts it off the content it is covering while it floats. Barely
        // visible once it has landed, which is the point.
        'shadow-[0_-6px_16px_-12px_rgb(0_0_0/0.35)]',
        className,
      )}
    >
      {start}
      {/* `ml-auto` rather than `justify-between`: with no `start` the actions
          still finish at the right edge instead of stretching across. */}
      <div className="ml-auto flex items-center gap-2">{children}</div>
    </div>
  );
}
